import { Mail, Phone, MapPin, Instagram, Facebook, Linkedin, Youtube } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useLanguage } from "@/contexts/LanguageContext";
import { useNavigate } from "react-router-dom";
import type { MouseEvent } from "react";
const Footer = () => {
  const {
    t
  } = useLanguage();
  const navigate = useNavigate();
  const navigation = [{
    name: t('nav.home'),
    href: "#home"
  }, {
    name: t('nav.products'),
    href: "/produtos"
  }, {
    name: t('nav.about'),
    href: "/about"
  }, {
    name: t('nav.blog'),
    href: "/blog"
  }, {
    name: t('nav.careers'),
    href: "https://os.quimical.com.br/pessoas"
    }, {
      name: t('nav.suppliers'),
      href: "/fornecedores"
    }, {
      name: t('nav.contact'),
      href: "/sac"
    }];
  const productLinks = [{
    name: t('footer.products.calcitica_quicklime'),
    href: "/produtos#cal-calcitica-virgem"
  }, {
    name: t('footer.products.calcitica_hydrated'),
    href: "/produtos#cal-calcitica-hidratada"
  }, {
    name: t('footer.products.dolomitic_quicklime'),
    href: "/produtos#cal-dolomitica-virgem"
  }, {
    name: t('footer.products.dolomitic_hydrated'),
    href: "/produtos#cal-dolomitica-hidratada"
  }];
  const socialLinks = [{
    icon: Instagram,
    href: "https://www.instagram.com/quimical_oficial/",
    name: "Instagram"
  }, {
    icon: Linkedin,
    href: "https://www.linkedin.com/company/quimicaloficial",
    name: "LinkedIn"
  }, {
    icon: Youtube,
    href: "https://www.youtube.com/@quimical_oficial",
    name: "YouTube"
  }, {
    icon: Facebook,
    href: "https://www.facebook.com/oficialquimical/",
      name: "Facebook"
    }];
  const plantLocations = [
    t('footer.plants.arcos'),
    t('footer.plants.corrego_fundo'),
    t('footer.plants.pains'),
    t('footer.plants.sao_jose_lapa'),
    t('footer.plants.colombo'),
    t('footer.plants.almirante_tamandare'),
    t('footer.plants.ponta_grossa'),
    t('footer.plants.euclides_cunha'),
    t('footer.plants.apodi')
  ];
  const legalLinks = [{
    name: t('footer.legal.privacy'),
    href: "/privacidade"
  }, {
    name: t('footer.legal.terms'),
    href: "/termos"
  }, {
    name: t('footer.legal.cookies'),
    href: "/cookies"
  }];
  const handleLinkClick = (e: MouseEvent<HTMLAnchorElement>, href: string) => {
    // Native navigation resolves product fragments after the destination page loads.
    if (href.includes("#") && href !== "#home") return;
    if (e.ctrlKey || e.metaKey || e.shiftKey || e.altKey || e.button !== 0) return;
    if (href === "#home") {
      e.preventDefault();
      navigate("/");
    } else if (href.startsWith('/')) {
      e.preventDefault();
      navigate(href);
    }
    setTimeout(() => window.scrollTo({ top: 0, behavior: 'smooth' }), 0);
  };
  return <footer className="bg-card border-t">
      <div className="container mx-auto px-4 py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-8">
          {/* Company Info */}
          <div className="space-y-4">
            <picture>
              <source srcSet="/media/logoespecialhome-new.jpg" type="image/jpeg" />
              <img src="/media/logoespecialhome-new.jpg" alt="Quimical" className="h-16 w-auto" width="64" height="64" />
            </picture>
            <p className="text-sm text-muted-foreground">
              {t('hero.subtitle')}
            </p>
            <div className="space-y-2">
              <a href="tel:08005917215" className="flex items-center text-sm text-muted-foreground hover:text-primary transition-colors">
                <Phone className="w-4 h-4 mr-2" />
                {t('contact.phone')}
              </a>
              <a href="mailto:cal@quimical.com.br" className="flex items-center text-sm text-muted-foreground hover:text-primary transition-colors">
                <Mail className="w-4 h-4 mr-2" />
                {t('contact.email')}
              </a>
              <div className="flex items-start text-sm text-muted-foreground">
                <MapPin className="w-4 h-4 mr-2 mt-0.5 flex-shrink-0" />
                <span>
                  {t('footer.address')}
                </span>
              </div>
            </div>
          </div>

          {/* Navigation */}
          <div className="space-y-4">
            <h3 className="font-semibold text-foreground">{t('footer.navigation')}</h3>
            <ul className="space-y-2">
              {navigation.map(item => <li key={item.name}>
                  <a
                    href={item.href}
                    onClick={(e) => handleLinkClick(e, item.href)}
                    className="text-sm text-muted-foreground hover:text-primary transition-colors cursor-pointer"
                  >
                    {item.name}
                  </a>
                </li>)}
            </ul>
          </div>

          {/* Products */}
          <div className="space-y-4">
            <h3 className="font-semibold text-foreground">{t('products.title')}</h3>
            <ul className="space-y-2">
              {productLinks.map(link => (
                <li key={link.name}>
                  <a
                    href={link.href}
                    onClick={(e) => handleLinkClick(e, link.href)}
                    className="text-sm text-muted-foreground hover:text-primary transition-colors"
                  >
                    {link.name}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Plant Locations */}
          <div className="space-y-4">
            <h3 className="font-semibold text-foreground">{t('footer.plants.title')}</h3>
            <ul className="space-y-2">
              {plantLocations.map(location => (
                <li key={location} className="text-sm text-muted-foreground">
                  {location}
                </li>
              ))}
            </ul>
          </div>

          {/* Social Media & CTA */}
          <div className="space-y-4">
            <h3 className="font-semibold text-foreground">{t('footer.connect')}</h3>
            <div className="flex space-x-4">
              {socialLinks.map(social => <a key={social.name} href={social.href} target="_blank" rel="noopener noreferrer" className="text-muted-foreground hover:text-primary transition-colors" aria-label={social.name}>
                  <social.icon className="w-5 h-5" />
                </a>)}
            </div>
            <Button className="bg-primary hover:bg-primary/90 text-primary-foreground shadow-soft" onClick={() => window.open('https://wa.me/5537998606150', '_blank')} aria-label="Entrar em contato via WhatsApp">
              {t('hero.cta')}
            </Button>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="mt-8 pt-8 border-t text-center">
          <div className="flex flex-wrap items-center justify-center gap-x-2 gap-y-1 text-xs text-muted-foreground">
            {legalLinks.map((link, index) => (
              <span key={link.name} className="flex items-center gap-x-2">
                <a
                  href={link.href}
                  onClick={(e) => handleLinkClick(e, link.href)}
                  className="hover:text-primary transition-colors"
                >
                  {link.name}
                </a>
                {index < legalLinks.length - 1 && <span aria-hidden="true">•</span>}
              </span>
            ))}
          </div>
          <p className="text-xs text-gray-300 mt-2">
            {t('footer.copyright')}
          </p>
          <p className="text-xs text-gray-300 mt-2">
            {t('footer.version')}
          </p>
        </div>
      </div>
    </footer>;
};
export default Footer;

